// src/lib/kbeanApi/getFreeContent.js
// Filepath: // src/lib/kbeanApi/getFreeContent.js

import { requestJson } from "../http/request";
import { ApiError } from "../http/errors";

function isDebugApi() {
  try {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("debugApi") === "1") return true;
      if (window.__KBEAN_DEBUG_API === true) return true;
    }
  } catch {}
  return String(process.env.REACT_APP_DEBUG_API || "")
    .toLowerCase()
    .trim() === "true";
}

function safeClone(obj) {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch {
    return obj;
  }
}

/**
 * Calls the "Get Free Content" endpoint.
 * Uses REACT_APP_GET_FREE_CONTENT as the URL.
 * Method: GET
 * Authorization: Bearer <token> (via requestJson withAuth).
 *
 * API contract you provided (shape reference):
 * - Response: Array of collections with artworksList
 *
 * @returns {Promise<Array<{
 *   collectionId: string,
 *   collectionName: string,
 *   artworksList: Array<{
 *     title: string,
 *     artistFirstName: string,
 *     artistLastName: string,
 *     sharesYouOwn: number,
 *     valueYouOwn: number,
 *     artworkId: string,
 *     artUrl: string,
 *     medium: string | null,
 *     year: string | null,
 *     artDescription: string | null,
 *     expectedMediaType: "Image" | "Video",
 *     allowWallpaper: boolean
 *   }>
 * }>>}
 */
export async function getFreeContent() {
  const url = process.env.REACT_APP_GET_FREE_CONTENT;
  if (!url) {
    throw new ApiError("Missing env REACT_APP_GET_FREE_CONTENT");
  }

  const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
  const debug = isDebugApi();

  if (debug) {
    console.groupCollapsed("[API] getFreeContent → request");
    console.log("GET", url);
    console.groupEnd();
  }

  try {
    const res = await requestJson(url, {
      method: "GET",
      withAuth: true,
      timeoutMs: 15000,
    });

    if (debug) {
      const elapsed =
        (typeof performance !== "undefined" ? performance.now() : Date.now()) - t0;

      const collectionsCount = Array.isArray(res) ? res.length : 0;
      const summary = (Array.isArray(res) ? res : []).map((c, i) => ({
        idx: i,
        collectionId: c?.collectionId,
        collectionName: c?.collectionName,
        artworks: Array.isArray(c?.artworksList) ? c.artworksList.length : 0,
      }));

      const firstCollection = Array.isArray(res) ? res[0] : null;
      const firstArtwork = firstCollection?.artworksList?.[0] ?? null;

      console.groupCollapsed("[API] getFreeContent → response");
      console.log("Elapsed (ms):", Math.round(elapsed));
      console.log("Collections:", collectionsCount);
      try {
        console.table(summary);
      } catch {
        console.log("Summary:", summary);
      }
      console.log("First collection keys:", Object.keys(firstCollection || {}));
      console.log("First artwork keys:", Object.keys(firstArtwork || {}));

      // Keep a global pointer for quick inspection in DevTools
      if (typeof window !== "undefined") {
        window.__KBEAN_LAST_FREE = safeClone(res);
      }
      console.groupEnd();
    }

    return res;
  } catch (err) {
    if (isDebugApi()) {
      console.groupCollapsed("[API] getFreeContent → error");
      console.error(err);
      console.groupEnd();
    }
    throw err;
  }
}
